import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Gift, Users, Percent, Copy, Check, Trophy, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ReferralPage({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [applying, setApplying] = useState(false);
  
  useEffect(() => {
    fetchStats();
  }, []);
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/referrals/my-stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyCode = () => {
    if (stats?.my_code) {
      navigator.clipboard.writeText(stats.my_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleApplyReferral = async (e) => {
    e.preventDefault();
    
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    try {
      setApplying(true);
      await axios.post(
        `${API}/referrals/apply`,
        { referral_code: referralCode.trim().toUpperCase() },
        { withCredentials: true }
      );
      
      toast.success('Referral code applied! You got 10% discount on your next course.');
      setReferralCode('');
      fetchStats();
    } catch (error) {
      console.error('Error applying referral:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply referral code');
    } finally {
      setApplying(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  const shareUrl = `${window.location.origin}?ref=${stats?.my_code}`;
  
  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="referral-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-premium-gold rounded-2xl mb-6">
            <Gift className="w-10 h-10 text-dark-purple" />
          </div>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Referral <span className="gold-gradient">Program</span>
          </h1>
          <p className="text-lg text-white/70">
            Invite friends and earn discounts on your courses!
          </p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/60 font-semibold mb-1">Total Referrals</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {stats?.total_referrals || 0}
                </p>
              </div>
              <div className="w-14 h-14 bg-premium-gold/20 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-premium-gold" strokeWidth={1.5} />
              </div>
            </div>
          </div>
          
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/60 font-semibold mb-1">Discount Earned</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {stats?.total_discount_earned || 0}%
                </p>
              </div>
              <div className="w-14 h-14 bg-premium-gold/20 rounded-xl flex items-center justify-center">
                <Percent className="w-7 h-7 text-premium-gold" />
              </div>
            </div>
          </div>
          
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/60 font-semibold mb-1">Max Discount</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {Math.min(stats?.total_discount_earned || 0, 50)}%
                </p>
              </div>
              <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <Trophy className="w-7 h-7 text-emerald-400" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>
        
        {/* My Referral Code */}
        <div className="bg-dark-surface p-8 rounded-xl border border-premium-gold/30/50 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3" style={{ fontFamily: 'Fraunces, serif' }}>
            <Sparkles className="w-6 h-6 text-premium-gold" />
            Your Referral Code
          </h2>
          <p className="text-white/60 mb-6">
            Share this code with friends. When they sign up and enroll in a course, you both get 10% discount!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 bg-deep-dark px-6 py-4 rounded-lg border-2 border-premium-gold/30">
              <p className="text-3xl font-black text-premium-gold tracking-wider" style={{ fontFamily: 'Fraunces, serif' }}>
                {stats?.my_code || 'Loading...'}
              </p>
            </div>
            <button
              onClick={handleCopyCode}
              className="bg-premium-gold hover:bg-premium-gold-dark text-white px-8 py-4 rounded-lg transition-all duration-300 hover:shadow-glow-red flex items-center justify-center gap-2 font-bold uppercase"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              {copied ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          
          <div className="mt-6">
            <p className="text-sm text-white/50 mb-2">Or share this link:</p>
            <div className="bg-deep-dark/50 px-4 py-3 rounded-lg border border-premium-gold/30/50 text-sm text-white/70 break-all">
              {shareUrl}
            </div>
          </div>
        </div>
        
        {/* Apply Referral Code */}
        {!stats?.my_referral_use && (
          <div className="bg-dark-surface p-8 rounded-xl border border-premium-gold/30/50 mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              Have a Referral Code?
            </h2>
            <p className="text-white/60 mb-6">
              Enter a friend's referral code to get 10% discount on your first course!
            </p>
            
            <form onSubmit={handleApplyReferral} className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Enter referral code"
                className="flex-1 px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all uppercase"
                data-testid="referral-input"
              />
              <button
                type="submit"
                disabled={applying || !referralCode.trim()}
                className="bg-premium-gold hover:bg-premium-gold-dark text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 hover:shadow-glow-red disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="apply-button"
              >
                {applying ? 'Applying...' : 'Apply Code'}
              </button>
            </form>
          </div>
        )}
        
        {/* My Referral Discount */}
        {stats?.my_referral_use && (
          <div className="bg-emerald-900/30 border-2 border-emerald-500/50 p-6 rounded-xl mb-8">
            <h3 className="text-xl font-bold mb-2 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              You Have a Discount!
            </h3>
            <p className="text-white/80">
              You have <span className="font-bold text-emerald-400">{stats.my_referral_use.discount_percentage}% discount</span> {stats.my_referral_use.used ? 'that was used' : 'ready to use'} on your next course enrollment.
            </p>
          </div>
        )}
        
        {/* Referral Activity */}
        {stats?.referral_uses && stats.referral_uses.length > 0 && (
          <div className="bg-dark-surface p-8 rounded-xl border border-premium-gold/30/50">
            <h2 className="text-2xl font-bold mb-6 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              Your Referrals
            </h2>
            <div className="space-y-3">
              {stats.referral_uses.map((use) => (
                <div key={use.use_id} className="flex items-center justify-between p-4 bg-deep-dark/50 rounded-lg border border-premium-gold/30/30">
                  <div>
                    <p className="font-semibold text-white">Friend signed up!</p>
                    <p className="text-sm text-white/50">{new Date(use.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-emerald-400">+{use.discount_percentage}%</p>
                    <p className="text-xs text-white/50">{use.used ? 'Used' : 'Available'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReferralPage;
